/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Form fields for creating an Expense Inventory case.
 * Pure UI — receives all dropdown data, selection state, and handlers from
 * the parent container (`ExpenseInventoryForm`).
 * @module src/components/Inventory/expense/form/ExpenseInventoryFormModal
 */
import React, { FC, useState } from 'react';
import { Button, Grid, Typography, makeStyles } from '@material-ui/core';

import AutocompleteSingleSelectPicker from '../../../Shared/AutocompleteSingleSelectPicker';
import { ResolvedFieldConfig, isFieldVisible, isFieldRequired } from './expenseInventoryConfig';
import DatePicker from '../../../Shared/DatePicker';
import RadioGroup from '../../../Shared/RadioGroup';
import TextArea from '../../../Shared/TextArea';

const WRITE_OFF_TYPE_OPTIONS = ['Demo', 'Lab', 'Reposable'];
const writeOffOptions = WRITE_OFF_TYPE_OPTIONS.map(option => ({
  id: option,
  value: option,
  label: option,
}));

const useStyles = makeStyles(theme => ({
  requiredNote: {
    marginBottom: theme.spacing(2),
  },
  fieldsContainer: {
    border: 'none',
    backgroundColor: '#fff',
    padding: theme.spacing(2, 1, 3, 2),
    marginBottom: theme.spacing(4),
  },
  stickyFooter: {
    // Sticks to the bottom of the modal's scrollable content area (the nearest
    // overflow:auto ancestor) so Save/Cancel stay visible without requiring
    // the user to scroll or zoom out to reach them.
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: '#f3f2f2',
    borderTop: '2px solid #dddbda',
    padding: theme.spacing(1.5, 2),
  },
  cancelButton: {
    color: '#080707',
    textTransform: 'none',
    height: 32,
    width: 64,
    minWidth: 64,
    fontSize: 13,
  },
  saveButton: {
    backgroundColor: '#FFB500',
    color: '#000',
    border: '1px solid #FFB500',
    textTransform: 'none',
    height: 32,
    width: 64,
    minWidth: 64,
    fontSize: 13,
    '&:hover': {
      backgroundColor: '#e6a300',
    },
    '&.Mui-disabled': {
      backgroundColor: '#c9c7c5',
      color: '#000',
      borderColor: '#c9c7c5',
      cursor: 'not-allowed',
    },
  },
}));

interface FormValues {
  usageDate: Date | null;
  usedInPatientCare: boolean | null;
  marketingCollaboration: boolean | null;
  doctorName: string;
  facilityNameOrLocation: string;
}

interface Props {
  writeOffType: string;
  onWriteOffTypeChange: (value: string) => void;
  fieldConfig: ResolvedFieldConfig;
  salesReps: any[];
  selectedSalesRep: any;
  onSalesRepChange: (value: any) => void;
  loadingSalesReps?: boolean;
  accounts: any[];
  selectedAccount: any;
  onAccountChange: (value: any) => void;
  loadingAccounts?: boolean;
  shipToAddresses: any[];
  selectedShipTo: any;
  onShipToChange: (value: any) => void;
  loadingShipTo?: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (values: FormValues) => void;
  initialValues?: Partial<FormValues>;
}

const YES = 'Yes';
const NO = 'No';
type YesNoValue = typeof YES | typeof NO | null;

const getYesNoValue = (value: boolean | null): YesNoValue => {
  if (value === null) return null;
  return value ? YES : NO;
};

const getMissingRequiredFields = (
  fieldConfig: ResolvedFieldConfig,
  values: FormValues
): {
  missingUsageDate: boolean;
  missingDoctorName: boolean;
  missingFacilityName: boolean;
  missingUsedInPatientCare: boolean;
  missingMarketingCollaboration: boolean;
} => ({
  missingUsageDate: isFieldRequired(fieldConfig, 'usageDate') && !values.usageDate,
  missingDoctorName: isFieldRequired(fieldConfig, 'doctorName') && !values.doctorName?.trim(),
  missingFacilityName:
    isFieldRequired(fieldConfig, 'facilityNameOrLocation') &&
    !values.facilityNameOrLocation?.trim(),
  missingUsedInPatientCare:
    isFieldRequired(fieldConfig, 'usedInPatientCare') && values.usedInPatientCare === null,
  missingMarketingCollaboration:
    isFieldRequired(fieldConfig, 'marketingCollaboration') &&
    values.marketingCollaboration === null,
});

const ExpenseInventoryFormModal: FC<Props> = ({
  writeOffType,
  onWriteOffTypeChange,
  fieldConfig,
  salesReps,
  selectedSalesRep,
  onSalesRepChange,
  loadingSalesReps,
  accounts,
  selectedAccount,
  onAccountChange,
  loadingAccounts,
  shipToAddresses,
  selectedShipTo,
  onShipToChange,
  loadingShipTo,
  isSaving,
  onCancel,
  onSubmit,
  initialValues,
}) => {
  const classes = useStyles();
  const [pickerTouched, setPickerTouched] = useState(false);

  const [formValues, setFormValues] = useState<FormValues>(() => ({
    usageDate: initialValues?.usageDate ?? null,
    usedInPatientCare:
      initialValues?.usedInPatientCare ??
      (isFieldRequired(fieldConfig, 'usedInPatientCare') ? null : false),
    marketingCollaboration:
      initialValues?.marketingCollaboration ??
      (isFieldRequired(fieldConfig, 'marketingCollaboration') ? null : false),
    doctorName: initialValues?.doctorName ?? '',
    facilityNameOrLocation: initialValues?.facilityNameOrLocation ?? '',
  }));

  const setFieldValue = <K extends keyof FormValues>(field: K, value: FormValues[K]): void => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  const missingSalesRep = isFieldRequired(fieldConfig, 'salesRep') && !selectedSalesRep;
  const missingAccount = isFieldRequired(fieldConfig, 'account') && !selectedAccount;
  const missingShipTo = isFieldRequired(fieldConfig, 'shipTo') && !selectedShipTo;
  const hasMissingPickers = missingSalesRep || missingAccount || missingShipTo;

  console.log(
    'isFieldRequired(fieldConfig, writeOffType):',
    isFieldRequired(fieldConfig, 'writeOffType')
  );

  const {
    missingUsageDate,
    missingDoctorName,
    missingFacilityName,
    missingUsedInPatientCare,
    missingMarketingCollaboration,
  } = getMissingRequiredFields(fieldConfig, formValues);
  const isSaveDisabled =
    isSaving ||
    !writeOffType ||
    hasMissingPickers ||
    missingUsageDate ||
    missingDoctorName ||
    missingFacilityName ||
    missingUsedInPatientCare ||
    missingMarketingCollaboration;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setPickerTouched(true);
    if (
      !writeOffType ||
      hasMissingPickers ||
      missingUsageDate ||
      missingDoctorName ||
      missingFacilityName ||
      missingUsedInPatientCare ||
      missingMarketingCollaboration
    ) {
      return;
    }
    onSubmit(formValues);
  };

  return (
    <form className="form-section" onSubmit={handleSubmit}>
      <Typography variant="body2" color="textSecondary" className={classes.requiredNote}>
        *Required fields
      </Typography>
      <Grid container spacing={1} className={classes.fieldsContainer}>
        {isFieldVisible(fieldConfig, 'writeOffType') && (
          <Grid item xs={12}>
            <AutocompleteSingleSelectPicker
              name="expenseInventoryWriteOffType"
              label="Write-Off Type"
              placeholder="Select Write-Off Type"
              items={writeOffOptions}
              selectedItem={writeOffOptions.filter(option => option.id === writeOffType)}
              setSelectedItem={(items: any): void => {
                onWriteOffTypeChange(items && items[0] ? items[0].value : '');
              }}
              withAddMenuButton={false}
              fetchItems={(): string => ''}
              filter={false}
              disabled={false}
              required={isFieldRequired(fieldConfig, 'writeOffType')}
              isLoading={false}
            ></AutocompleteSingleSelectPicker>
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'salesRep') && (
          <Grid item xs={12}>
            <AutocompleteSingleSelectPicker
              name="expenseInventorySalesRep"
              label="Sales Rep"
              placeholder="Select Sales Rep"
              items={salesReps}
              selectedItem={selectedSalesRep ? [selectedSalesRep] : []}
              setSelectedItem={(items: any): void =>
                onSalesRepChange(items && items[0] ? items[0] : null)
              }
              withAddMenuButton={false}
              fetchItems={(): string => ''}
              filter={false}
              disabled={false}
              isLoading={loadingSalesReps}
              required={isFieldRequired(fieldConfig, 'salesRep')}
            />
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'account') && (
          <Grid item xs={12}>
            <AutocompleteSingleSelectPicker
              key={selectedSalesRep?.id ?? 'account-empty'}
              name="expenseInventoryAccount"
              label="Account"
              placeholder="Select Account"
              items={accounts}
              selectedItem={selectedSalesRep && selectedAccount ? [selectedAccount] : []}
              setSelectedItem={(items: any): void =>
                onAccountChange(items && items[0] ? items[0] : null)
              }
              withAddMenuButton={false}
              fetchItems={(): string => ''}
              filter={false}
              disabled={!selectedSalesRep}
              isLoading={loadingAccounts}
              required={isFieldRequired(fieldConfig, 'account')}
            />
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'shipTo') && (
          <Grid item xs={12}>
            <AutocompleteSingleSelectPicker
              key={`${selectedSalesRep?.id ?? 'none'}-${selectedAccount?.id ?? 'none'}`}
              name="expenseInventoryShipTo"
              label="Ship To"
              placeholder="Select Ship To"
              items={shipToAddresses}
              selectedItem={
                selectedSalesRep && selectedAccount && selectedShipTo ? [selectedShipTo] : []
              }
              setSelectedItem={(items: any): void =>
                onShipToChange(items && items[0] ? items[0] : null)
              }
              withAddMenuButton={false}
              fetchItems={(): string => ''}
              filter={false}
              disabled={!selectedSalesRep || !selectedAccount}
              isLoading={loadingShipTo}
              required={isFieldRequired(fieldConfig, 'shipTo')}
            />
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'usageDate') && (
          <Grid item xs={12}>
            <DatePicker
              label="Usage Date"
              placeholder="Pick a Date"
              key="usageDate"
              disabled={false}
              isFilterDate={true}
              required={isFieldRequired(fieldConfig, 'usageDate')}
              value={formValues.usageDate ? formValues.usageDate : ''}
              handleChangeDate={(result: any): void => {
                setFieldValue('usageDate', result ? new Date(result) : null);
              }}
            />
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'usedInPatientCare') && (
          <Grid item xs={12}>
            <RadioGroup
              label="Was the product used in patient care?"
              items={[
                { label: NO, value: NO, id: 'NoPatient' },
                { label: YES, value: YES, id: 'YesPatient' },
              ]}
              defaultValue={getYesNoValue(formValues.usedInPatientCare)}
              required={isFieldRequired(fieldConfig, 'usedInPatientCare')}
              handleRadioButtonChange={(event): void =>
                setFieldValue('usedInPatientCare', event.target.value === YES)
              }
            />
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'marketingCollaboration') && (
          <Grid item xs={12}>
            <RadioGroup
              label="Was there collaboration with Marketing?"
              items={[
                { label: NO, value: false, id: 'NoMarketing' },
                { label: YES, value: true, id: 'YesMarketing' },
              ]}
              defaultValue={getYesNoValue(formValues.marketingCollaboration)}
              required={isFieldRequired(fieldConfig, 'marketingCollaboration')}
              handleRadioButtonChange={(event): void =>
                setFieldValue('marketingCollaboration', event.target.value === YES)
              }
            />
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'doctorName') && (
          <Grid item xs={12}>
            <TextArea
              label="Doctor Name"
              maxLength={'250'}
              showTooltip={false}
              tooltipText={''}
              key={'doctorName'}
              value={formValues.doctorName || ''}
              required={isFieldRequired(fieldConfig, 'doctorName')}
              disabled={false}
              handleChangeText={(result: any): void => {
                setFieldValue('doctorName', result || '');
              }}
            />
          </Grid>
        )}
        {isFieldVisible(fieldConfig, 'facilityNameOrLocation') && (
          <Grid item xs={12}>
            <TextArea
              label="Facility Name or Location"
              maxLength={'250'}
              showTooltip={false}
              tooltipText={''}
              key={'facilityNameOrLocation'}
              value={formValues.facilityNameOrLocation || ''}
              required={isFieldRequired(fieldConfig, 'facilityNameOrLocation')}
              disabled={false}
              handleChangeText={(result: any): void => {
                setFieldValue('facilityNameOrLocation', result || '');
              }}
            />
          </Grid>
        )}
      </Grid>
      <Grid className={classes.stickyFooter}>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          disabled={isSaving}
          className={classes.cancelButton}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSaveDisabled}
          className={classes.saveButton}
        >
          Save
        </Button>
      </Grid>
    </form>
  );
};

export default ExpenseInventoryFormModal;
